import { useEffect } from "react";

import { getUnreadCount, isMessengerOpen } from "./messenger-state";
import { hasPassiveNotification } from "./passive-notifications";

// Side-effect import: the rules keyed on LAUNCHER_VISIBLE_CLASS below. It has
// to reach the eager CSS bundle, or the launcher flashes up before the class
// that hides it exists — this module is pulled in by routes.tsx, which is.
import "./intercom.css";

/**
 * Whether the launcher is shown is ours to decide, not Intercom's.
 *
 * Its own `hide_default_launcher` removes the launcher element from the
 * document entirely, and with nothing to badge Intercom stops emitting unread
 * events — so a reply arriving mid-session never surfaced. Keeping the element
 * mounted and merely invisible also takes `Intercom("update")` out of the
 * visibility path, which is what used to re-render the messenger back to its
 * Home tab and throw the user out of the conversation they were reading.
 *
 * The rules this class drives live in ./intercom.css, imported below.
 */
const LAUNCHER_VISIBLE_CLASS = "weld-intercom-launcher-visible";

/** Whether the current route asked for Intercom's own launcher. */
let launcherRequested = false;

function launcherShouldHide() {
  // Never yank the launcher out from under an open messenger: while the panel
  // is open it doubles as the minimise control. onHide re-evaluates.
  //
  // A passive notification counts the same as an unread reply: a ticket
  // update is just as much something the user is waiting on, and hiding the
  // launcher would leave its dot floating with nothing to click.
  return (
    !launcherRequested &&
    getUnreadCount() === 0 &&
    !hasPassiveNotification() &&
    !isMessengerOpen()
  );
}

export function syncLauncherVisibility() {
  // Purely a class toggle, so it does not have to wait for the widget script
  // and cannot race the boot that drains the loader queue.
  document.documentElement.classList.toggle(
    LAUNCHER_VISIBLE_CLASS,
    !launcherShouldHide(),
  );
}

function setLauncherVisible(visible: boolean) {
  if (launcherRequested === visible) return;
  launcherRequested = visible;
  syncLauncherVisibility();
}

/**
 * Shows Intercom's own launcher for as long as the calling component is
 * mounted. Used on the routes outside a workspace — onboarding and workspace
 * creation — where there is no sidebar to hang a "Chat with us" item off.
 */
export function useIntercomLauncher(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return;
    setLauncherVisible(true);
    return () => setLauncherVisible(false);
  }, [enabled]);
}
